import React, { ReactNode, useState } from 'react';
import { Box as Wrapper, Flex, Text, Collapse } from '@tonic-ui/react';
import styles from './collapsible-cards.module.css';

interface CardProps {
  id: string;
  heading: ReactNode;
  className?: string;
  icon?: ReactNode;
  body?: ReactNode;
  isExpanded: boolean;
  onClick: (id: string) => void;
}
interface CollapsibleCardsProps {
  cards: {
    id: string;
    heading: ReactNode;
    className?: string;
    icon?: ReactNode;
    body?: ReactNode;
  }[];
}

const CollapseExpandSign = ({ isExpanded }) => {
  return (
    <Wrapper
      className={
        isExpanded
          ? `${styles.collapsible_sign} ${styles.collapsible_sign_expanded}`
          : styles.collapsible_sign
      }
    >
      <div />
      <div />
    </Wrapper>
  );
};

const Card = ({
  id,
  isExpanded,
  heading,
  onClick,
  icon,
  className = '',
}: CardProps): JSX.Element => (
  <Wrapper
    backgroundColor="blue:50"
    padding="6x"
    margin="0 0.5px"
    className={className}
    flex="1"
    whiteSpace="pre-wrap"
    borderRadius="lg"
    width="320px"
    style={{ cursor: 'pointer', minHeight: 268 }}
    onClick={() => onClick(id)}
  >
    <Flex direction="column" justify="space-around" height="100%">
      <Flex direction="column" justify="flex-start" flex="1">
        <Flex flex="0" height="28px">
          {icon}
        </Flex>
        <Flex flex="0" alignItems="center" pb="6x" pt="6x">
          <Text fontWeight="light" lineHeight="1.6" size="2xl" color="white:primary">
            {heading}
          </Text>
        </Flex>
      </Flex>
      <Flex flex="0" alignSelf="flex-end">
        <CollapseExpandSign isExpanded={isExpanded} />
      </Flex>
    </Flex>
  </Wrapper>
);

const CollapsibleCards = ({ cards = [] }: CollapsibleCardsProps): JSX.Element => {
  const [expandedCardId, setExpandedCardId] = useState<string>();

  const handleOnCardToggle = (id: string) => {
    setExpandedCardId(expandedCardId === id ? '' : id);
  };

  return (
    <Wrapper width="100%">
      <Flex direction="row" style={{ verticalAlign: 'baseline' }}>
        {cards.map(({ heading, id, icon, ...otherProps }) => (
          <Card
            {...otherProps}
            key={`collapse-card-${id}`}
            heading={heading}
            id={id}
            icon={icon}
            isExpanded={expandedCardId === id}
            onClick={handleOnCardToggle}
          />
        ))}
      </Flex>
      {cards.map(({ id, body }) => (
        <Collapse key={`collapse-${id}`} in={expandedCardId === id}>
          <Flex height="450px">{expandedCardId === id && body}</Flex>
        </Collapse>
      ))}
    </Wrapper>
  );
};

CollapsibleCards.displayName = 'CollapsibleCards';

export default CollapsibleCards;
